import React, { useState, ChangeEvent } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  useTheme,
  Paper,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Upload, Save, Close, Download } from '@mui/icons-material';
import { SiMicrosoftexcel } from 'react-icons/si';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'app/hooks';
import { importRecords } from '../../slices/incomeExpenses/IncomeExpensesSlice';

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
}

interface RawRecord {
  [key: string]: string | number | undefined;
}

const IncomeExpensesImportModal: React.FC<ImportModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );

  /* ------------------------------------------------------------------ */
  /*                             STATE                                  */
  /* ------------------------------------------------------------------ */
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /* ------------------------------------------------------------------ */
  /*                    BANK TRANSACTIONS TEMPLATE DOWNLOAD             */
  /* ------------------------------------------------------------------ */
  const downloadBankTransactionsTemplate = async () => {
    try {
      setLoading(true);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Bank Transactions');

      // Define headers based on your CSV structure
      const headers = [
        'Date',
        'Income_Expense',
        'Main_tag',
        'Identity',
        'payment codeA',
        'payment codeB',
        'Reason_paymentA',
        'Reason_paymentB'
      ];

      // Add header row
      const headerRow = worksheet.addRow(headers);

      // Add example data from your CSV file
      const exampleRows = [
        [
          '22-07-2025',
          '-488',
          'bank record 01',
          'john doe 01',
          'activity 01',
          'same 1',
          'activity 01',
          'activity 01'
        ],
        [
          '22-07-2025',
          '-10',
          'bank record 02',
          'john doe 02',
          'activity 02',
          'same 2',
          'activity 02',
          'activity 02'
        ],
        [
          '21-07-2025',
          '-1000',
          'bank record 03',
          'john doe 03',
          'activity 03',
          'same 3',
          'activity 03',
          'activity 03'
        ],
        [
          '14-07-2025',
          '-20',
          'bank record 04',
          'john doe 04',
          'activity 04',
          'same 4',
          'activity 04',
          'activity 04'
        ],
        [
          '07-07-2025',
          '-1079.18',
          'bank record 05',
          'john doe 05',
          'activity 05',
          'same 5',
          'activity 05',
          'activity 05'
        ],
        [
          '03-07-2025',
          '945.42',
          'bank record 06',
          'john doe 06',
          'activity 06',
          'same 6',
          'activity 06',
          'activity 06'
        ],
        [
          '20-06-2025',
          '540.06',
          'bank record 07',
          'john doe 07',
          'activity 07',
          'same 7',
          'activity 07',
          'activity 07'
        ],
        [
          '05-06-2025',
          '-500',
          'bank record 08',
          'john doe 08',
          'activity 08',
          'same 8',
          'activity 08',
          'activity 08'
        ],
        [
          '05-06-2025',
          '540.06',
          'bank record 09',
          'john doe 09',
          'activity 09',
          'same 9',
          'activity 09',
          'activity 09'
        ],
        [
          '02-06-2025',
          '-855.89',
          'bank record 10',
          'john doe 10',
          'activity 10',
          'same 10',
          'activity 10',
          'activity 10'
        ]
      ];

      // Add example rows
      exampleRows.forEach((row) => {
        worksheet.addRow(row);
      });

      // Add 200 empty rows for data entry
      for (let i = 0; i < 200; i++) {
        worksheet.addRow(Array(headers.length).fill(''));
      }

      // Set column widths
      worksheet.columns = headers.map((header, index) => ({
        header: header,
        key: header.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        width:
          [
            12, // Date
            15, // Income_Expense
            20, // Main_tag
            20, // Identity
            18, // payment codeA
            18, // payment codeB
            20, // Reason_paymentA
            20 // Reason_paymentB
          ][index] || 15
      }));

      // Style header row
      headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;

      // Add borders to all cells
      const totalRows = exampleRows.length + 200 + 1; // examples + empty + header
      for (let rowNumber = 1; rowNumber <= totalRows; rowNumber++) {
        const row = worksheet.getRow(rowNumber);
        for (let colNumber = 1; colNumber <= headers.length; colNumber++) {
          const cell = row.getCell(colNumber);
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

          // Highlight example rows with light blue
          if (rowNumber > 1 && rowNumber <= exampleRows.length + 1) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFE3F2FD' }
            };
          }
        }
      }

      // Freeze header row
      worksheet.views = [
        {
          state: 'frozen',
          ySplit: 1
        }
      ];

      // Create comprehensive instructions worksheet
      const instructionsWS = workbook.addWorksheet('📋 Instructions & Guide');

      const instructionsData = [
        ['BANK TRANSACTIONS IMPORT TEMPLATE'],
        [''],
        ['📊 FIELD DESCRIPTIONS:'],
        ['Field Name', 'Description', 'Format/Example', 'Required'],
        ['Date', 'Transaction date', 'DD-MM-YYYY (22-07-2025)', 'Yes'],
        [
          'Income_Expense',
          'Transaction amount',
          'Positive for income, negative for expense (-488, 945.42)',
          'Yes'
        ],
        ['Main_tag', 'Primary record identifier', 'bank record 01, bank record 02, etc.', 'Yes'],
        ['Identity', 'Person or entity name', 'john doe 01, john doe 02, etc.', 'Yes'],
        ['payment codeA', 'Primary payment code', 'activity 01, activity 02, etc.', 'No'],
        ['payment codeB', 'Secondary payment code', 'same 1, same 2, etc.', 'No'],
        ['Reason_paymentA', 'Primary payment reason', 'activity 01, activity 02, etc.', 'No'],
        ['Reason_paymentB', 'Secondary payment reason', 'activity 01, activity 02, etc.', 'No'],
        [''],
        ['💡 DATA ENTRY GUIDELINES:'],
        ['Rule', 'Description', 'Example'],
        ['Date Format', 'Use DD-MM-YYYY format with hyphens', '22-07-2025, 14-07-2025'],
        ['Income Values', 'Use positive numbers for income', '945.42, 540.06'],
        ['Expense Values', 'Use negative numbers for expenses', '-488, -1079.18'],
        ['Decimal Places', 'Use up to 2 decimal places for amounts', '-1079.18, 945.42'],
        ['Text Fields', 'Use consistent naming patterns', 'bank record 01, bank record 02'],
        ['Empty Cells', 'Leave optional fields blank if no data', ''],
        [''],
        ['📈 TRANSACTION EXAMPLES:'],
        ['Type', 'Date', 'Amount', 'Description'],
        ['Income', '22-07-2025', '945.42', 'Positive amount for money received'],
        ['Expense', '22-07-2025', '-488', 'Negative amount for money spent'],
        ['Large Expense', '21-07-2025', '-1000', 'Major transaction'],
        ['Small Income', '20-06-2025', '540.06', 'Regular income entry'],
        [''],
        ['⚠️ IMPORTANT REQUIREMENTS:'],
        ['Requirement', 'Details', 'Why Important'],
        [
          'Required Fields',
          'Date, Income_Expense, Main_tag, Identity must be filled',
          'System needs these to process transactions'
        ],
        ['Date Consistency', 'Always use DD-MM-YYYY format', 'Prevents date parsing errors'],
        ['Amount Format', 'Use numbers only (no currency symbols)', 'Enables proper calculations'],
        ['File Format', 'Save as .xlsx or .csv', 'Ensures compatibility with import system'],
        ['No Empty Rows', 'Remove blank rows between data', 'Prevents import interruption'],
        [''],
        ['🚀 IMPORT PROCESS:'],
        ['Step', 'Action', 'Result'],
        ['1', 'Fill template with transaction data', 'Data structured correctly'],
        ['2', 'Verify required fields are complete', 'No missing critical data'],
        ['3', 'Check date and amount formats', 'Proper data types'],
        ['4', 'Save file as .xlsx or .csv', 'Compatible file format'],
        ['5', 'Use import feature in application', 'Transactions loaded into system'],
        [''],
        ['💾 FILE FORMAT SUPPORT:'],
        ['Format', 'Extension', 'Notes'],
        ['Excel Workbook', '.xlsx', 'Recommended - preserves formatting'],
        ['Excel 97-2003', '.xls', 'Supported - older Excel format'],
        ['Comma Separated Values', '.csv', 'Supported - simple text format']
      ];

      // Add instructions data to worksheet
      instructionsData.forEach((row, index) => {
        const wsRow = instructionsWS.addRow(row);

        // Style different sections
        if (index === 0) {
          // Main title
          wsRow.font = { bold: true, size: 16, color: { argb: 'FF000000' } };
          wsRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
          wsRow.height = 30;
        } else if (
          typeof row[0] === 'string' &&
          (row[0].includes('📊') ||
            row[0].includes('💡') ||
            row[0].includes('📈') ||
            row[0].includes('⚠️') ||
            row[0].includes('🚀') ||
            row[0].includes('💾'))
        ) {
          // Section headers
          wsRow.font = { bold: true, size: 12, color: { argb: 'FF000000' } };
          wsRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
          wsRow.height = 25;
        } else if (
          row[0] === 'Field Name' ||
          row[0] === 'Rule' ||
          row[0] === 'Type' ||
          row[0] === 'Requirement' ||
          row[0] === 'Step' ||
          row[0] === 'Format'
        ) {
          // Table headers
          wsRow.font = { bold: true, size: 10 };
          wsRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7E6E6' } };
        }

        // Add borders to non-empty rows
        if (row[0]) {
          wsRow.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        }
      });

      // Set column widths for instructions
      instructionsWS.columns = [{ width: 25 }, { width: 45 }, { width: 35 }, { width: 15 }];

      // Generate the Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Bank_Transactions_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setError(null);
      setLoading(false);

      setTimeout(() => {
        setSuccess(false);
      }, 4000);
    } catch (err) {
      console.error('Error creating bank transactions template:', err);
      setError('Failed to generate template. Please try again.');
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*                        FILE PARSING LOGIC                          */
  /* ------------------------------------------------------------------ */
  const processFile = async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Validate file extension
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext || '')) {
        throw new Error('Unsupported file type. Please upload a .csv, .xlsx, or .xls file.');
      }

      // Read file
      let workbook: XLSX.WorkBook;
      if (ext === 'csv') {
        const csvText = await file.text();
        workbook = XLSX.read(csvText, { type: 'string' });
      } else {
        const data = new Uint8Array(await file.arrayBuffer());
        workbook = XLSX.read(data, { type: 'array' });
      }

      // Extract data from the first sheet, using first row as headers
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonRows: RawRecord[] = XLSX.utils.sheet_to_json(sheet, {
        defval: '',
        blankrows: false
      });

      console.log('✅ Parsed JSON rows:', jsonRows);

      // Validate required fields
      const requiredFields = ['Date', 'Income_Expense', 'Main_tag', 'Identity'];
      const missingFields = requiredFields.filter(
        (field) => !jsonRows[0] || !Object.keys(jsonRows[0]).includes(field)
      );

      if (missingFields.length > 0) {
        throw new Error(
          `Missing required columns: ${missingFields.join(', ')}. Please use the provided template.`
        );
      }

      // Transform data to match your expected format
      const transformedRecords = jsonRows.map((row) => ({
        date: row['Date'],
        income_expense: Number(row['Income_Expense']) || 0,
        main_tag: row['Main_tag'],
        identity: row['Identity'],
        payment_code_a: row['payment codeA'] || '',
        payment_code_b: row['payment codeB'] || '',
        reason_payment_a: row['Reason_paymentA'] || '',
        reason_payment_b: row['Reason_paymentB'] || ''
      }));

      console.log('✅ Transformed records:', transformedRecords);

      // Dispatch transformed JSON data to importRecords API
      dispatch(
        importRecords({
          group_id: selectedProjectId || '',
          service_id: selectedServiceTitle || '',
          records: transformedRecords
        })
      );

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process the file.');
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*                        UI EVENT HANDLERS                           */
  /* ------------------------------------------------------------------ */
  const storeFile = (file: File) => {
    setSelectedFile(file);
    setError(null);
    setSuccess(false);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) storeFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) storeFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
  };

  const handleDone = async () => {
    if (selectedFile) {
      await processFile(selectedFile);
      if (success && !error) {
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /*                              JSX                                   */
  /* ------------------------------------------------------------------ */
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 600 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          outline: 'none',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Header with Download Template Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2" fontWeight={600}>
            Import Bank Transactions
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <Download />}
              onClick={downloadBankTransactionsTemplate}
              disabled={loading}
              size="small"
              sx={{
                textTransform: 'none',
                borderColor: '#217346',
                color: '#217346',
                '&:hover': {
                  borderColor: '#1a5d37',
                  backgroundColor: '#f0f8f0'
                }
              }}
            >
              <SiMicrosoftexcel
                style={{
                  marginRight: '4px',
                  fontSize: '1rem',
                  color: loading ? '#999999' : '#217346'
                }}
              />
              {loading ? 'Generating...' : 'Download Template'}
            </Button>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Body */}
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Download the Excel template to import bank transactions with the correct format and
            structure.
          </Typography>

          {/* Template Features */}
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0 }}>
              <li>
                <strong>📅 Date Field:</strong> Transaction dates in DD-MM-YYYY format
              </li>
              <li>
                <strong>💰 Income/Expense:</strong> Positive for income, negative for expenses
              </li>
              <li>
                <strong>🏷️ Tags & Identity:</strong> Record identifiers and person names
              </li>
              <li>
                <strong>🔢 Payment Codes:</strong> Primary and secondary payment classifications
              </li>
              <li>
                <strong>📝 Reason Fields:</strong> Payment reason descriptions
              </li>
              <li>
                <strong>📊 Example Data:</strong> 10 sample transactions included
              </li>
              <li>
                <strong>📋 Instructions:</strong> Complete usage guide with field explanations
              </li>
            </Typography>
          </Box>

          {/* Success/Error Alerts */}
          {success && (
            <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
              {selectedFile
                ? '🎉 Bank transactions imported successfully!'
                : '📁 Bank transactions template downloaded successfully!'}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
              ❌ {error}
            </Alert>
          )}

          {/* Drag-and-drop zone */}
          <Paper
            elevation={dragOver ? 8 : 2}
            sx={{
              p: 3,
              textAlign: 'center',
              border: `2px dashed ${
                dragOver ? theme.palette.primary.main : theme.palette.grey[400]
              }`,
              bgcolor: dragOver ? theme.palette.primary.light : theme.palette.background.paper,
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setDragOver(false)}
          >
            <Stack spacing={2} alignItems="center">
              <Upload sx={{ fontSize: 40, color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {dragOver
                  ? '📁 Drop your bank transactions file here'
                  : 'Drag and drop your bank transactions file or click to upload'}
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<Upload />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Browse Files
                <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFileUpload} />
              </Button>
              <Typography variant="caption" color="text.secondary">
                Supports .xlsx, .xls, .csv (Use template above for best results)
              </Typography>
              {selectedFile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    mt: 2
                  }}
                >
                  <SiMicrosoftexcel color="#217346" style={{ fontSize: '1.5rem' }} />
                  <Typography variant="body2" color="text.primary" fontWeight={500}>
                    📄 Selected: {selectedFile.name}
                  </Typography>
                  <IconButton size="small" onClick={handleClearFile}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Footer buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Close sx={{ fontSize: '1rem' }} />}
              fullWidth
              onClick={onClose}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : (
                  <Save sx={{ fontSize: '1rem' }} />
                )
              }
              fullWidth
              disabled={!selectedFile || loading}
              onClick={handleDone}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#217346',
                '&:hover': {
                  backgroundColor: '#1a5d37'
                }
              }}
            >
              {loading ? 'Processing...' : 'Import Transactions'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default IncomeExpensesImportModal;
